import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const Modal = ({
  isOpen,
  onClose,
  title,
  icon: Icon,
  iconColor = 'text-light-on-primary-container dark:text-dark-on-primary-container', // Colore icona personalizzabile
  children,
  size = 'md', // sm, md, lg, xl
  showCloseButton = true,
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  useEffect(() => {
    if (isOpen) {
      // Previene lo scroll del body e mantiene la posizione corrente
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      setIsClosing(false);
    } else {
      // Ripristina lo scroll e la posizione
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    }
    
    return () => {
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  if (!isOpen) return null;

  // Size classes mapping con gestione responsive
  const sizeClasses = {
    sm: 'max-w-[90%] sm:max-w-md',
    md: 'max-w-[95%] sm:max-w-lg',
    lg: 'max-w-[98%] sm:max-w-2xl',
    xl: 'max-w-[99%] sm:max-w-4xl'
  };

  const modalContent = (
    <div 
      className={`
        fixed inset-0 z-50 
        flex items-center justify-center
        transition-opacity duration-200 ease-in-out
        ${isClosing ? 'opacity-0' : 'opacity-100'}
        bg-black/50
        overscroll-contain
      `}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div 
        className={`
          relative w-full m-4
          ${sizeClasses[size]}
          bg-light-surface dark:bg-dark-surface
          rounded-lg shadow-surface-container
          transition-all duration-200 ease-in-out
          ${isClosing 
            ? 'opacity-0 translate-y-4 scale-95' 
            : 'opacity-100 translate-y-0 scale-100'
          }
          max-h-[calc(100vh-2rem)]
          flex flex-col
        `}
      >
        {/* Header */}
        <div className="
          flex items-center justify-between p-4 
          border-b border-light-outline-variant dark:border-dark-outline-variant
          shrink-0
        ">
          <div className="flex items-center gap-3 min-w-0">
            {Icon && (
              <div className={`shrink-0 ${iconColor}`}>
                <Icon size={20} strokeWidth={2} />
              </div>
            )}
            <h3 className="
              text-base sm:text-lg font-semibold 
              text-light-on-surface dark:text-dark-on-surface
              truncate
            ">
              {title}
            </h3>
          </div>
          {showCloseButton && (
            <button
              onClick={handleClose}
              className="
                p-2 rounded-full shrink-0
                text-light-on-surface-variant dark:text-dark-on-surface-variant
                hover:bg-light-surface-container dark:hover:bg-dark-surface-container
                active:bg-light-surface-container-high dark:active:bg-dark-surface-container-high
                transition-colors duration-200
              "
              aria-label="Chiudi modale"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="
          flex-1 p-4 
          overflow-y-auto
          overscroll-contain
          relative
        ">
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;